import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import course1 from '../assets/images/courses1.webp';
import course2 from '../assets/images/Courses3.webp';
import course3 from '../assets/images/Courses2.webp';
import course4 from '../assets/images/Courses4.webp';
import course5 from '../assets/images/Courses5.webp';

const courses = [
  {
    id: 'business-chinese',
    image: course1,
    title: 'Business Chinese',
    description: 'Empower professionals and corporate employees to enhance their Chinese communication, negotiation, and intercultural competence, enabling them to thrive in diverse, cross-cultural business environments.',
    suitableFor: [
      'Foreign professionals',
      'Multinational company employees',
      'International students preparing to study or work in China'
    ],
    color: '#07284b',
    pricing: {
      online: '$80+',
      offline: '$100+',
      duration: '60 minutes',
      groupSize: '1 person',
      note: 'Small group: $50/person (2-4 people)'
    }
  },
  {
    id: 'exam-preparation',
    image: course2,
    title: 'Exam Preparation',
    description: 'Equip students with targeted preparation, mastery of key knowledge, and effective exam strategies to achieve outstanding results.',
    suitableFor: [
      'Students preparing for local or international Chinese proficiency exams',
      'Candidates seeking rapid score improvement or a structured review program',
    ],
    color: '#9f2122',
    pricing: {
      online: '$80+',
      offline: '$100+',
      duration: '90 minutes',
      groupSize: '1-6 people',
      note: 'Group discounts available'
    }
  },
  {
    id: 'speech-eloquence',
    image: course3,
    title: 'Speech & Eloquence',
    description: 'Develop students’ Chinese expression skills, logical thinking, and stage presence, empowering them to speak confidently and express themselves boldly.',
    suitableFor: [
      'School-age children and teenagers',
      'Learners aiming to improve Mandarin proficiency, public speaking, and performance skills',
    ],
    color: '#07284b',
    pricing: {
      online: '$70+',
      offline: '$90+',
      duration: '60 minutes',
      groupSize: '1-4 people',
      note: 'Starting from $80/person'
    }
  },
  {
    id: 'immersion-programs',
    image: course4,
    title: 'Immersion Programs',
    description: 'Enable students to “live in Chinese” in a relaxed environment, fully experience Chinese language and culture, and cultivate authentic, practical Chinese communication skills.',
    suitableFor: [
      'School-age children and teenagers',
      'Learners interested in Chinese language and culture',
      'Students aiming to “live in Chinese” in an immersive, relaxed environment',
      'Learners seeking to improve practical language skills, cultural understanding, and cross-cultural adaptability'
    ],
    color: '#9f2122',
    pricing: {
      online: '',
      offline: '$500+/day',
      duration: '6 hours',
      groupSize: '2-6 people',
      note: 'Intensive cultural workshops included'
    }
  },
  {
    id: 'tailored-programs',
    image: course5,
    title: 'Tailored Programs',
    description: 'Design personalized learning programs tailored to learners’ goals, interests, and schedules, ensuring efficient and targeted progress.',
    suitableFor: [
      'Corporate executives, expatriate families, and individuals with clear learning objectives',
      'Students requiring flexible scheduling or one-on-one guidance',
    ],
    color: '#07284b',
    pricing: {
      online: 'Custom',
      offline: 'Custom',
      duration: 'Flexible',
      groupSize: 'Individual/Group',
      note: 'Contact for pricing'
    }
  }
];

function Courses() {
  const handleCourseClick = (courseId) => {
    // 直接跳转到对应的课程详情页面
    window.location.href = `/courses#${courseId}`;
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 12 },
        background: 'linear-gradient(45deg, rgba(135, 206, 250, 0.1) 0%, rgba(255, 182, 193, 0.08) 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'rgba(255, 182, 193, 0.15)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '15%',
          left: '8%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(255, 182, 193, 0.12)',
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        {/* 标题区域 */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              color: '#07284b',
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2
            }}
          >
            Our Courses
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#34495e',
              maxWidth: 900,
              mx: 'auto',
              fontSize: '1.2rem',
              lineHeight: 1.6
            }}
          >
            Our courses range from beginner to advanced levels, designed for children, teens, and adults. We offer one-on-one instruction, premium small-group classes, and customized corporate training.
          </Typography>
        </Box>

        {/* 课程卡片网格 */}
        <Grid container justifyContent="center" spacing={4}>
          {courses.map((course, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  '&:hover .course-details-modal': {
                    opacity: 1,
                    visibility: 'visible',
                    transform: 'translateY(0)',
                  }
                }}
              >
                <Card
                  onClick={() => handleCourseClick(course.id)}
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                      background: 'rgba(255,255,255,0.98)',
                      '& .course-icon': {
                        transform: 'scale(1.15) rotate(5deg)',
                      },
                      '&::before': {
                        opacity: 1
                      },
                      '& .course-hover-overlay': {
                        opacity: 1,
                        visibility: 'visible'
                      },
                      '& .course-hover-text': {
                        opacity: 1,
                        transform: 'translateX(-50%) translateY(-50%) translateY(0)',
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      },
                      '& .more-arrow': {
                        opacity: 1,
                        transform: 'translateX(8px) scale(1.2)'
                      },
                      '& .course-title': {
                        color: course.color,
                        transform: 'translateY(-2px)'
                      },
                      '& .course-description': {
                        color: '#2c3e50',
                        transform: 'translateY(-1px)'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${course.color}08 0%, ${course.color}15 100%)`,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      zIndex: 1
                    }
                  }}
                >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
                  {/* 上方内容区域 */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    flexGrow: 1,
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 0 }
                  }}>
                    {/* 图片区域 */}
                    <Box
                      className="course-icon"
                      sx={{
                        flexShrink: 0,
                        mr: { xs: 0, md: 3 },
                        mb: { xs: 2, md: 0 },
                        transition: 'transform 0.3s ease',
                        order: { xs: 1, md: 1 }
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 100, md: 120 },
                          height: { xs: 100, md: 120 },
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${course.color}15 0%, ${course.color}25 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `2px solid ${course.color}20`,
                          overflow: 'hidden',
                          mx: 'auto'
                        }}
                      >
                        <Box
                          component="img"
                          src={course.image}
                          alt={course.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* 文字内容 */}
                    <Box sx={{ 
                      flexGrow: 1, 
                      minWidth: 0,
                      order: { xs: 2, md: 2 }
                    }}>
                      {/* 标题 */}
                      <Typography
                        className="course-title"
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          color: '#2c3e50',
                          mb: 1,
                          transition: 'all 0.3s ease',
                          fontSize: '1.2rem',
                          lineHeight: 1.3
                        }}
                      >
                        {course.title}
                      </Typography>

                      {/* 描述 */}
                      <Typography
                        className="course-description"
                        variant="body2"
                        sx={{
                          color: '#34495e',
                          lineHeight: 1.5,
                          fontSize: '0.9rem',
                          mb: 1,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {course.description}
                      </Typography>

                      {/* Suitable For - 仅对Business Chinese显示 */}
                      {course.suitableFor && (
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#2c3e50',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            Suitable for:
                          </Typography>
                          <Box component="ul" sx={{ 
                            pl: 2, 
                            m: 0,
                            '& li': {
                              color: '#34495e',
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              mb: 0.5
                            }
                          }}>
                            {course.suitableFor.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </Box>
                        </Box>
                      )}


                      {/* More 文字和箭头 */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: course.suitableFor ? 1.5 : 2,
                          opacity: 0.7,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: course.color,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            mr: 1
                          }}
                        >
                          More
                        </Typography>
                        <Box
                          className="more-arrow"
                          sx={{
                            width: 0,
                            height: 0,
                            borderLeft: `6px solid ${course.color}`,
                            borderTop: '4px solid transparent',
                            borderBottom: '4px solid transparent',
                            transition: 'all 0.3s ease',
                            opacity: 0.7
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* 装饰线条 - 居中显示 */}
                  <Box
                    sx={{
                      width: '60px',
                      height: '3px',
                      background: `linear-gradient(90deg, ${course.color} 0%, ${course.color}80 100%)`,
                      borderRadius: '2px',
                      mx: 'auto',
                      mt: 3
                    }}
                  />

                  {/* 悬停覆盖层 */}
                  <Box
                    className="course-hover-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 50%)`,
                      opacity: 0,
                      visibility: 'hidden',
                      transition: 'all 0.3s ease',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />

                  {/* 悬停提示文字 */}
                  <Box
                    className="course-hover-text"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translateX(-50%) translateY(-50%) translateY(20px)',
                      opacity: 0,
                      transition: 'all 0.3s ease',
                      zIndex: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {/* 主按钮 */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCourseClick(course.id);
                      }}
                      sx={{
                        background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`,
                        color: 'white',
                        px: { xs: 2, md: 4 },
                        py: 2,
                        borderRadius: 4,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                        backdropFilter: 'blur(12px)',
                        border: '2px solid rgba(255,255,255,0.2)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px) scale(1.05)',
                          boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                          background: `linear-gradient(135deg, ${course.color}dd 0%, ${course.color} 100%)`
                        },
                        '&:active': {
                          transform: 'translateY(0) scale(0.98)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: 'white'
                        }}
                      >
                        View Details
                        <Box
                          sx={{
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid white',
                            borderTop: '4px solid transparent',
                            borderBottom: '4px solid transparent',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* 副标题 */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textAlign: 'center',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        letterSpacing: '0.3px',
                        opacity: 0.9
                      }}
                    >
                      Click to explore course details
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            </Grid>
          ))}
        </Grid>

        {/* 底部说明文字 */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#34495e',
              fontSize: '1.1rem',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 4
            }}
          >
            All courses are designed with flexible scheduling and personalized learning paths 
            to ensure the best learning experience for every student.
          </Typography>

          {/* Call to Action Button */}
          <Button
            variant="contained"
            href="/courses"
            sx={{
              bgcolor: '#9f2122',
              color: 'white',
              px: 5,
              py: 2,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 4px 20px rgba(159, 33, 34, 0.3)',
              '&:hover': {
                bgcolor: '#8a1e1f',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 25px rgba(159, 33, 34, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            View More Course Details
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default Courses;
